<template>
  <SuperAdminShell>
    <div class="super-admin-page sa-users-page">
      <header class="super-admin-page__header">
        <div class="super-admin-page__header-lead">
          <h3>Usuários do Super Admin</h3>
          <small>Crie e consulte quem acessa este painel</small>
        </div>
        <div class="super-admin-page__header-actions">
          <button
            v-if="activeTab === 'form'"
            type="button"
            class="btnSave btnFillPreview"
            @click="fillPreviewData"
          >
            Preencher dados
          </button>
          <button
            v-if="activeTab === 'list'"
            type="button"
            class="btnSave"
            @click="openCreate"
          >
            Novo usuário
          </button>
          <button
            v-else
            type="button"
            class="btnSave btnCancel"
            @click="openList"
          >
            Voltar
          </button>
        </div>
      </header>

      <div class="super-admin-page__shell">
        <div class="super-admin-steps">
          <nav class="super-admin-tabs sa-users-tabs" aria-label="Seções de usuários">
            <button
              type="button"
              class="super-admin-tab"
              :class="{ 'super-admin-tab--active': activeTab === 'list' }"
              @click="openList"
            >
              <span class="super-admin-tab__index">1</span>
              <span class="super-admin-tab__copy">
                <strong>Usuários</strong>
                <small>Listagem de acessos</small>
              </span>
            </button>
            <button
              type="button"
              class="super-admin-tab"
              :class="{ 'super-admin-tab--active': activeTab === 'form' }"
              @click="onFormTab"
            >
              <span class="super-admin-tab__index">2</span>
              <span class="super-admin-tab__copy">
                <strong>{{ editingId ? 'Editar usuário' : 'Novo usuário' }}</strong>
                <small>Dados do cadastro</small>
              </span>
            </button>
          </nav>

          <div
            v-show="activeTab === 'list'"
            class="super-admin-page__panel super-admin-page__panel--tabbed"
          >
            <div class="section-title">
              <h4>Listagem</h4>
              <p>Usuários com permissão de super admin.</p>
            </div>

            <div class="sa-list-filters">
              <div class="sa-list-filters__field sa-list-filters__field--grow">
                <label for="sa-user-search">Busca</label>
                <input
                  id="sa-user-search"
                  v-model="searchTerm"
                  type="text"
                  class="sa-list-filters__control"
                  placeholder="Nome ou e-mail"
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__field">
                <label for="sa-user-status">Status</label>
                <select
                  id="sa-user-status"
                  v-model="statusFilter"
                  class="sa-list-filters__control"
                  @change="onFilterChange"
                >
                  <option value="">Todos</option>
                  <option value="1">Ativo</option>
                  <option value="2">Inativo</option>
                </select>
              </div>
              <div class="sa-list-filters__actions">
                <button
                  type="button"
                  class="sa-list-filters__clear"
                  :disabled="!hasActiveFilters"
                  @click="clearFilters"
                >
                  Limpar filtros
                </button>
              </div>
            </div>

            <p v-if="listError" class="sa-users-state" role="alert">
              {{ listError }}
              <button type="button" class="sa-users-retry" @click="loadList">Tentar novamente</button>
            </p>
            <p v-else-if="loadingList" class="sa-users-state">Carregando usuários...</p>

            <template v-else>
              <div v-if="!users.length" class="sa-list-page__empty">
                <h3>Nenhum usuário encontrado</h3>
                <p>Altere os filtros ou cadastre um novo acesso.</p>
                <button type="button" class="btnSave" @click="openCreate">Novo usuário</button>
              </div>

              <template v-else>
                <div class="sa-rep-table-wrap">
                  <table class="sa-rep-table">
                    <thead>
                      <tr>
                        <th scope="col" class="is-id">ID</th>
                        <th scope="col" class="is-company">Nome</th>
                        <th scope="col">E-mail</th>
                        <th scope="col">Telefone</th>
                        <th scope="col">Status</th>
                        <th scope="col" class="is-date">Cadastro</th>
                        <th scope="col">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="user in users" :key="user.id">
                        <td class="is-id is-muted">{{ user.id }}</td>
                        <td class="is-company">
                          <span class="sa-rep-table__name">{{ user.name || '—' }}</span>
                        </td>
                        <td class="is-muted">{{ user.email || '—' }}</td>
                        <td class="is-muted">{{ user.phone || '—' }}</td>
                        <td>
                          <span class="sa-users-status" :class="statusClass(user.status)">
                            {{ statusLabel(user.status) }}
                          </span>
                        </td>
                        <td class="is-date">{{ formatDateBR(user.dateInsert) }}</td>
                        <td>
                          <button
                            type="button"
                            class="sa-list-action-link"
                            title="Editar usuário"
                            @click="openEdit(user)"
                          >
                            <fa icon="fa-solid fa-pen" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <ReportsPagination
                  :page="currentPage"
                  :total-pages="totalPages"
                  :page-size="pageSize"
                  show-single-page
                  @update:page="onPageChange"
                  @update:pageSize="onPageSizeChange"
                />
              </template>
            </template>
          </div>

          <div
            v-show="activeTab === 'form'"
            class="super-admin-page__panel super-admin-page__panel--tabbed"
          >
            <div class="section-title">
              <h4>{{ editingId ? 'Editar usuário' : 'Novo usuário' }}</h4>
              <p>
                {{ editingId
                  ? 'Atualize os dados do usuário.'
                  : 'Preencha os dados do usuário. O acesso é criado como super admin e a senha é definida pelo link enviado no e-mail.' }}
              </p>
            </div>

            <p v-if="loadingUser" class="sa-users-state">Carregando usuário...</p>

            <form v-else @submit.prevent="saveUser">
              <div class="section-title">
                <h4>Acesso</h4>
                <p>Nome e e-mail usados para entrar no painel.</p>
              </div>
              <div class="row">
                <div class="col-form-inner col-6 col-md-6 col-lg-6">
                  <div class="form-group">
                    <div class="form-label">
                      <label for="sa-user-name">Nome</label>
                    </div>
                    <div class="form-input">
                      <input
                        id="sa-user-name"
                        v-model="form.name"
                        type="text"
                        class="form-control"
                        autocomplete="name"
                        placeholder="Nome completo"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-form-inner col-6 col-md-6 col-lg-6">
                  <div class="form-group">
                    <div class="form-label">
                      <label for="sa-user-email">E-mail</label>
                    </div>
                    <div class="form-input">
                      <input
                        id="sa-user-email"
                        v-model="form.email"
                        type="email"
                        class="form-control"
                        autocomplete="off"
                        placeholder="nome@empresa.com"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-form-inner col-6 col-md-6 col-lg-6">
                  <div class="form-group">
                    <div class="form-label">
                      <label for="sa-user-phone">Telefone</label>
                    </div>
                    <div class="form-input">
                      <input
                        id="sa-user-phone"
                        v-model="form.phone"
                        v-mask="'(##) #####-####'"
                        type="text"
                        class="form-control"
                        autocomplete="tel"
                        placeholder="(21) 99999-9999"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-form-inner col-6 col-md-6 col-lg-6">
                  <div class="form-group">
                    <div class="form-label">
                      <label for="sa-user-form-status">Status</label>
                    </div>
                    <div class="form-input">
                      <select id="sa-user-form-status" v-model.number="form.status" class="form-control">
                        <option :value="1">Ativo</option>
                        <option :value="2">Inativo</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="section-title">
                <h4>Documentos</h4>
                <p>CPF enviado na criação do usuário.</p>
              </div>
              <div class="row">
                <div class="col-form-inner col-6 col-md-6 col-lg-6">
                  <div class="form-group">
                    <div class="form-label">
                      <label for="sa-user-cpf">CPF</label>
                    </div>
                    <div class="form-input">
                      <input
                        id="sa-user-cpf"
                        v-model="form.socialNumber"
                        v-mask="'###.###.###-##'"
                        type="text"
                        class="form-control"
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="actionsSave">
                <button type="button" class="btnSave btnCancel" :disabled="saving" @click="openList">
                  Cancelar
                </button>
                <button type="submit" class="btnSave" :disabled="saving">
                  {{ saving ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </SuperAdminShell>
</template>

<script>
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue';
import ReportsPagination from '@/components/Weflix/SuperAdmin/Reports/ReportsPagination.vue';
import userApi from '@/utils/user';
import superAdmin from '@/utils/superAdmin';
import { formatDateBR } from '@/utils/superAdmin/formatters';

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

function emptyForm() {
  return {
    name: '',
    email: '',
    phone: '',
    status: 1,
    socialNumber: '',
  };
}

function apiMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return fallback;
}

export default {
  name: 'SuperAdminUsers',
  components: {
    SuperAdminShell,
    ReportsPagination,
  },
  data() {
    return {
      activeTab: 'list',
      users: [],
      totalElements: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 10,
      searchTerm: '',
      statusFilter: '',
      loadingList: true,
      listError: '',
      loadingUser: false,
      saving: false,
      editingId: null,
      form: emptyForm(),
      searchTimer: null,
    };
  },
  computed: {
    ...mapState(['token']),
    hasActiveFilters() {
      return Boolean(this.searchTerm || this.statusFilter);
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.syncFromRoute();
      },
    },
  },
  created() {
    this.loadList();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatDateBR,
    fillPreviewData() {
      const stamp = Date.now();
      this.form = {
        ...this.form,
        name: 'Tony Stark',
        email: `tstark+${stamp}@avengers.com`,
        phone: '(21) 99999-9999',
        status: 1,
        socialNumber: '262.244.519-90',
      };
    },
    statusLabel(status) {
      if (Number(status) === 1) return 'Ativo';
      if (Number(status) === 2) return 'Inativo';
      return 'Neutro';
    },
    statusClass(status) {
      if (Number(status) === 1) return 'is-active';
      if (Number(status) === 2) return 'is-inactive';
      return '';
    },
    openList() {
      this.resetForm();
      this.$router.replace({ path: '/admin/usuarios', query: {} }).catch(() => {});
    },
    onFormTab() {
      if (this.activeTab === 'form') return;
      this.openCreate();
    },
    openCreate() {
      this.resetForm();
      this.$router.replace({ path: '/admin/usuarios', query: { tab: 'cadastro' } }).catch(() => {});
    },
    openEdit(user) {
      this.$router.replace({
        path: '/admin/usuarios',
        query: { tab: 'cadastro', id: user.id },
      }).catch(() => {});
    },
    resetForm() {
      this.editingId = null;
      this.form = emptyForm();
      this.loadingUser = false;
    },
    async syncFromRoute() {
      const tab = String(this.$route.query.tab || '');
      const id = this.$route.query.id;
      if (tab !== 'cadastro') {
        this.activeTab = 'list';
        return;
      }
      this.activeTab = 'form';
      if (!id) {
        if (this.editingId) this.resetForm();
        return;
      }
      if (String(this.editingId) === String(id)) return;
      await this.loadUser(id);
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadList();
      }, 300);
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadList();
    },
    clearFilters() {
      this.searchTerm = '';
      this.statusFilter = '';
      this.currentPage = 1;
      this.loadList();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadList();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.loadList();
    },
    async loadList() {
      this.loadingList = true;
      this.listError = '';
      try {
        const response = await superAdmin.listSuperAdminUsers(this.token, {
          page: this.currentPage - 1,
          size: this.pageSize,
          search: this.searchTerm,
          status: this.statusFilter,
        });
        const content = Array.isArray(response)
          ? response
          : (Array.isArray(response?.content) ? response.content : []);
        const pageMeta = response?.page && typeof response.page === 'object'
          ? response.page
          : response;
        const pageSize = Number(pageMeta?.size) || this.pageSize;
        const totalElements = Number(pageMeta?.totalElements ?? content.length);
        const reportedPages = Number(pageMeta?.totalPages);
        this.users = content;
        this.totalElements = totalElements;
        this.totalPages = reportedPages > 0
          ? reportedPages
          : Math.max(Math.ceil(totalElements / pageSize), 1);
      } catch (error) {
        console.error('Erro ao listar usuários do super admin:', error);
        this.users = [];
        this.totalElements = 0;
        this.listError = apiMessage(error, 'Não foi possível carregar os usuários.');
      } finally {
        this.loadingList = false;
      }
    },
    async loadUser(id) {
      this.loadingUser = true;
      this.editingId = id;
      try {
        const user = await userApi.getDataUser(id, this.token);
        if (String(this.$route.query.id || '') !== String(id)) return;
        this.form = {
          name: user?.name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          status: Number(user?.status) === 2 ? 2 : 1,
          socialNumber: user?.socialNumber || '',
        };
      } catch (error) {
        if (String(this.$route.query.id || '') !== String(id)) return;
        console.error('Erro ao carregar usuário do super admin:', error);
        toast.error(apiMessage(error, 'Não foi possível carregar o usuário.'));
        this.openList();
      } finally {
        this.loadingUser = false;
      }
    },
    validateForm() {
      const name = this.form.name.trim();
      const email = this.form.email.trim();
      if (!name) return 'Informe o nome.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Informe um e-mail válido.';
      if (digits(this.form.socialNumber).length !== 11) return 'Informe um CPF válido.';
      return '';
    },
    async saveUser() {
      const message = this.validateForm();
      if (message) {
        toast.error(message);
        return;
      }
      const payload = {
        name: this.form.name.trim(),
        email: this.form.email.trim(),
        phone: digits(this.form.phone),
        socialNumber: digits(this.form.socialNumber),
        status: Number(this.form.status) === 2 ? 2 : 1,
        type: 1,
      };

      this.saving = true;
      try {
        if (this.editingId) {
          await superAdmin.updateSuperAdminUser(this.token, this.editingId, payload);
          toast.success('Usuário atualizado.');
        } else {
          await superAdmin.createSuperAdminUser(this.token, payload);
          toast.success('Usuário criado.');
        }
        this.resetForm();
        this.openList();
        this.currentPage = 1;
        await this.loadList();
      } catch (error) {
        console.error('Erro ao salvar usuário do super admin:', error);
        toast.error(apiMessage(error, 'Não foi possível salvar o usuário.'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('@/css/weflix/super-admin-list.css');
@import url('@/components/Weflix/SuperAdmin/Reports/report-table.css');

.sa-users-page {
  --dashboard-bg: #f6f7fb;
  --dashboard-card: #ffffff;
  --dashboard-border: #e8eaf0;
  --dashboard-text: #191c24;
  --dashboard-muted: #697386;
  --dashboard-primary: #250891;
}

.sa-users-tabs {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.sa-users-state {
  margin: 0 0 1rem;
  color: #5c6370;
  font-size: 0.875rem;
}

.sa-users-retry {
  margin-left: 0.5rem;
  border: 0;
  background: transparent;
  color: #250891;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.sa-users-status {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: #eef0fc;
  color: #5c6370;
  font-size: 0.75rem;
  font-weight: 700;
}

.sa-users-status.is-active {
  background: #ecfdf3;
  color: #027a48;
}

.sa-users-status.is-inactive {
  background: #fef3f2;
  color: #b42318;
}

.sa-users-page .form-group > small {
  display: block;
  margin-top: 0.35rem;
}

.sa-users-page .sa-list-action-link {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
</style>
